import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './TemplatesTypes.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { motion } from "motion/react";

const brokerageTemplates = [
    { id: 'brokerage-base', title: 'Base', description: 'Solid foundation with essential features and clean design.', img: require('../../assets/imgs/previews/brokerage/base.jpg') },
    { id: 'brokerage-luxury', title: 'Luxury', description: 'Elegant and sophisticated, ideal for high-end clients.', img: require('../../assets/imgs/previews/brokerage/luxury.jpg') },
    { id: 'brokerage-metropolitan', title: 'Metropolitan', description: 'Modern visuals with neutral tones and polish.', img: require('../../assets/imgs/previews/brokerage/metropolitan.jpg') },
    { id: 'brokerage-skyline', title: 'Skyline', description: 'Highlights condo living and cityscapes.', img: require('../../assets/imgs/previews/brokerage/skyline.jpg') },
    { id: 'brokerage-vibrance', title: 'Vibrance', description: 'Energetic and bold with asymmetrical design.', img: require('../../assets/imgs/previews/brokerage/vibrance.jpg') },
    { id: 'brokerage-futuristic', title: 'Futuristic', description: 'Neon and high-tech aesthetics for modern appeal.', img: require('../../assets/imgs/previews/brokerage/futuristic.jpg') },
    { id: 'brokerage-coastal', title: 'Coastal', description: 'Relaxed and breezy with a beach-inspired palette.', img: require('../../assets/imgs/previews/brokerage/coastal.jpg') },
    { id: 'brokerage-rural', title: 'Rural', description: 'Peaceful and serene with natural landscapes.', img: require('../../assets/imgs/previews/brokerage/rural.jpg') },
    { id: 'brokerage-nature', title: 'Nature', description: 'Organic and earthy with natural textures.', img: require('../../assets/imgs/previews/brokerage/nature.jpg') },
    { id: 'brokerage-noir', title: 'Noir', description: 'Sophisticated and elegant with a dark color palette.', img: require('../../assets/imgs/previews/brokerage/noir.jpg') },
    { id: 'brokerage-cottage', title: 'Cottage', description: 'A warm and cozy design with rustic charm and inviting natural elements.', img: require('../../assets/imgs/previews/brokerage/cottage.jpg') },
    { id: 'brokerage-harbour', title: 'Harbour', description: 'A fresh coastal-inspired design with clean layouts and calming waterfront aesthetics.', img: require('../../assets/imgs/previews/brokerage/harbour.jpg') },
    { id: 'brokerage-heritage', title: 'Heritage', description: 'A timeless design featuring classic architecture and elegant traditional details.', img: require('../../assets/imgs/previews/brokerage/heritage.jpg') },
    { id: 'brokerage-estate', title: 'Estate', description: 'A refined design with spacious layouts and a polished, upscale feel.', img: require('../../assets/imgs/previews/brokerage/estate.jpg') },
    { id: 'brokerage-signature', title: 'Signature', description: 'A premium design with bold styling and a distinctive, professional look.', img: require('../../assets/imgs/previews/brokerage/signature.jpg') },
    { id: 'brokerage-summit', title: 'Summit', description: 'A sleek, contemporary design with bold visuals and a confident, elevated style.', img: require('../../assets/imgs/previews/brokerage/summit.jpg') },
];

const BrokerageTemplates = () => {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredTemplates = brokerageTemplates.filter(template =>
        template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => a.title.localeCompare(b.title));

    return (
        <>
            <Helmet>
                <title>Brokerage Website Templates | AptoWebsites</title>
                <meta name="description" content="Browse professional website templates for real estate brokerages. Team pages, listings, and branding options included." />
                <meta name="keywords" content="brokerage website templates, real estate brokerage, team websites, AptoWebsites" />
                <meta property="og:title" content="Brokerage Website Templates | AptoWebsites" />
                <meta property="og:description" content="Browse professional website templates for real estate brokerages. Team pages, listings, and branding options included." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://aptowebsites.com/brokerage-templates" />
                <meta property="og:image" content="https://aptowebsites.com/og-image.jpg" />
                <link rel="canonical" href="https://aptowebsites.com/brokerage-templates" />
            </Helmet>
            <div className="templatebackground">
                <div className="template-header-bar professional">
                    <div className="template-header-titles">
                        <h1 className="template-header-title">Brokerage Website Templates</h1>
                        <p className="template-header-subtitle">
                            Professional team-ready layouts built for growth, authority, and multi-agent operations.
                        </p>
                    </div>
                    <div className="template-header-controls">
                        <div className="template-search-group">
                            <div className='icon-wrapper'>
                                <FontAwesomeIcon icon={faSearch} className='search-icon' />
                            </div>
                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="filter-input template-search-input"
                            />
                        </div>
                        <div className="template-results-count" aria-live="polite">
                            {filteredTemplates.length} templates
                        </div>
                    </div>
                </div>
                <div className="template-section">
                    <div className="template-list">
                        {filteredTemplates.map(template => {
                            let mobileImgSrc;
                            try {
                                mobileImgSrc = require(`../../assets/imgs/previews/mobile/brokerage/${template.id.replace('brokerage-', '')}.jpg`);
                            } catch (e) {
                                mobileImgSrc = template.img;
                            }
                            return (
                                <motion.div
                                    key={template.id}
                                    className="template-box"
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.45, ease: "easeOut" }}
                                    whileHover="hover"
                                    animate="rest"
                                >
                                    <Link to={`/${template.id}`}>
                                        <motion.div className="template-img-wrapper overlap"
                                        >
                                            <img
                                                src={require('../../assets/imgs/mockups/laptop-mockup1.png')}
                                                alt="Laptop Mockup"
                                                className="mockup-frame"
                                            />

                                            <div className="screen-img">
                                                <img
                                                    src={template.img}
                                                    alt={template.title}
                                                    className="template-img main-img"
                                                />
                                            </div>
                                            <div className="phone-mockup-container overlap">
                                                <img
                                                    src={require('../../assets/imgs/mockups/phone-mockup-transparent.png')}
                                                    alt="Phone Mockup"
                                                    className="phone-mockup-img"
                                                />
                                                <img
                                                    src={mobileImgSrc}
                                                    alt={`${template.title} mobile`}
                                                    className="template-img phone-inside-img"
                                                />
                                            </div>
                                        </motion.div>
                                        <span className="template-card-label">{template.title}</span>
                                        <motion.div
                                            className="template-info overlay"
                                            variants={{
                                                rest: {
                                                    y: 0
                                                },
                                                hover: {
                                                    y: -4
                                                }
                                            }}

                                            transition={{ duration: 0.4, ease: "easeInOut" }}
                                        >
                                            <h3 className="template-title">{template.title}</h3>

                                            <div className="template-extra">
                                                <p>{template.description}</p>
                                            </div>
                                        </motion.div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

export default BrokerageTemplates;
